import React, { useEffect, useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../../shared/utils/tableUtils"
import { MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { fetchSupportOrdersPOS, SupportOrdersPOSResponse, SupportReportsOrdersPPOSQuery } from "../posApi"
import { OrdersPOSType } from "./posTypes"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { OrderIdInput } from "../../../../../supplier/reporting/accounting/components/inputFilters/OrderIdInput"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { useExportPOSOrdersToCsv } from "../hooks/useExportPosOrders"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import useActiveFiltersCountAccounting from "../../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { StandardDateRangeFilter, type DtDateRange } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const selectOrdersPOS = (data: SupportOrdersPOSResponse) => data?.orders || []

export function OrdersPOSList() {
    const { l10n } = useAppLocalization()

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRows, setSelectedRows] = useState<OrdersPOSType[]>()
    const [receipt, setReceipt] = useState<Receipt>()

    const [drawerOpen, setDrawerOpen] = useState(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
    })
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const tableInstanceRef = useRef<MRT_TableInstance<OrdersPOSType>>(null)

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the user searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const ordersPOSResult = useApiQuery(fetchSupportOrdersPOS, {
        queryName: SupportReportsOrdersPPOSQuery.ORDERS_POS_FOR_SUPPORT,
        dependencies: {
            ...filtersList,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: isSearchingByOrderId ? undefined : dateState.startDate?.toISODate(),
            endDate: isSearchingByOrderId ? undefined : dateState.endDate?.toISODate(),
        },
        select: selectOrdersPOS,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(ordersPOSResult)

    const columns = useMemo<MRT_ColumnDef<OrdersPOSType>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("support-orders-pos-table-header-order-id"),
                accessorKey: "id",
            }),

            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-orders-pos-table-header-transaction-time"),
                    accessorKey: "created",
                },
                (row) => row?.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("support-orders-pos-table-header-customer"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={row.original.customer.displayName}
                        subtitle1={row.original.customer.id}
                        subtitle2={row.original.customer.email}
                    />
                ),
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-orders-pos-table-header-merchant"),
                accessorKey: "sellerSupplierParty.name",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("support-orders-pos-table-header-amount"),
                    id: "amount",
                },
                (row) => row.amount,
            ),
        ],
        [l10n, columnFactory],
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    function showReceipt(row: OrdersPOSType) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const { handleExportRows } = useExportPOSOrdersToCsv(
        dateState.startDate?.toISODate() ?? "",
        dateState.endDate?.toISODate() ?? "",
    )

    return (
        <>
            <LocalizedStrict id="support-orders-pos-title" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPORT_ORDERS_POS} title="Orders">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-orders">
                            <Breadcrumb>Orders</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-orders-pos">
                            <Breadcrumb>POS</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                onRowSelectionChange={setRowSelection}
                enableRowSelection
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="support-orders-pos-table-action-show-receipt">
                        <MenuItem
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                            key={`show-receipt-${row.original.id}`}
                        >
                            Show receipt
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="support-orders-pos-table-action-show-order-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`show-order-details-${row.original.id}`}
                        >
                            Show order details
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                        <StandardExportButton
                            onClick={() => {
                                handleExportRows(tableInstanceRef.current?.getRowModel().rows)
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter
                            value={dateState}
                            onChange={setDateState}
                        />
                        <OrderIdInput
                            value={filtersList.orderId ?? ""}
                            onChange={(orderId) => {
                                setFiltersList((prev) => ({
                                    ...prev,
                                    orderId,
                                }))
                            }}
                        />
                    </StandardTableFilter>
                )}
                state={{
                    rowSelection,
                }}
            />

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={getFilteredTableData(tableInstanceRef)}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}

            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="supplier-meeting-service-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() => handleExportRows(tableInstanceRef.current?.getSelectedRowModel().rows)}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
